import thumbnailIndex from "./thumbnails.json";
import { SEED_ALPHABET } from "../Helpers.tsx";

/**
 * A picture of page one of a worksheet page's own sheet (ADR 0069).
 *
 * The images are rendered offline by `npm run thumbnails` and committed under
 * public/thumbnails/; this index is written by the same script. `fingerprint`
 * is a hash of the settings the image was drawn from, which is how
 * thumbnails.test.ts notices an image that no longer matches its page.
 */
export interface Thumbnail {
    src: string;
    width: number;
    height: number;
    fingerprint: string;
}

const THUMBNAILS: Record<string, Thumbnail> = thumbnailIndex;

/** The image for a page, if it has one. */
export function thumbnailFor(slug: string): Thumbnail | undefined {
    return THUMBNAILS[slug];
}

/** Every page that has an image, by slug. */
export function allThumbnails(): Record<string, Thumbnail> {
    return THUMBNAILS;
}

/** 32-bit FNV-1a: small, dependency-free, and the same in Node and a browser. */
function fnv1a(text: string, basis: number = 0x811c9dc5): number {
    let hash = basis;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
}

/**
 * A hash of the settings an image was drawn from. It changes when a page's
 * preset does, and it does not see a change in how a format is drawn: that
 * needs a regeneration by hand, like re-measuring (ADR 0069 §4).
 */
export function thumbnailFingerprint(settings: unknown): string {
    return fnv1a(JSON.stringify(settings)).toString(16).padStart(8, "0");
}

/**
 * The seed a page's image is drawn with: six characters from the alphabet
 * `newSeed` uses, derived from the slug, so the pictured problems are stable
 * across regenerations and the code printed in the image's footer rebuilds
 * the pictured sheet on that page (ADR 0069 §2).
 */
export function thumbnailSeed(slug: string): string {
    let seed = "";
    let hash = fnv1a(slug);
    for (let i = 0; i < 6; i++) {
        seed += SEED_ALPHABET[hash % SEED_ALPHABET.length];
        hash = fnv1a(slug + seed, hash);
    }
    return seed;
}
